/**
 * AIProfileCoach — VIP-only top-level tab in Profile navigation.
 *
 * Sep 2026 · new suite. Does NOT duplicate:
 *   - Meet/Keep/Reputation scores (owned by /profile-optimizer + credibility.py)
 *   - Existing Improvement Tips (this page EXTENDS them, doesn't replace)
 *   - Existing AI Safety Chat at /ai-coach (chat/opener coach, different feature)
 *
 * Sections (all VIP-gated at the API layer):
 *   1. AI Profile Review     — full profile critique + focus areas
 *   2. AI Photo Analysis     — per-photo vision critique
 *   3. AI Photo Order        — best-order recommendation
 *   4. Photo Mix             — variety check
 *   5. AI Bio Coach          — analyze / improve / generate
 *   6. AI Prompt Coach       — analyze / improve / suggest per prompt
 *   7. AI Profile Optimizer  — proposed change list w/ per-item approval
 *   8. AI Photo Enhancement  — Nano Banana conservative enhance
 *   9. AI Dating Photos      — Nano Banana generation from references
 *
 * Free/Premium users see the same layout with locked "Unlock with VIP" cards.
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '@/contexts/AuthContext';
import {
  ArrowLeft, Sparkles, Camera, FileText, MessageSquare, Wand2, ImagePlus,
  Loader2, Lock, Crown, CheckCircle2, RefreshCw, ArrowRight,
} from 'lucide-react';

const APP_BG = '#F5F5F7';
const RED = '#DC2626';

/* ------------------------------------------------------------------ */
function Section({ icon: Icon, title, subtitle, locked, children, testId }) {
  return (
    <div className="bg-white rounded-2xl border border-black/[0.06] p-5" data-testid={testId}>
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center flex-shrink-0">
          <Icon size={18} style={{ color: RED }} strokeWidth={2} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-[16px] font-bold text-zinc-900">{title}</h2>
            {locked && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-900" data-testid={`${testId}-lock-badge`}>
                <Crown size={10} strokeWidth={2.5} /> VIP
              </span>
            )}
          </div>
          {subtitle && <p className="text-[13px] text-zinc-600 mt-0.5 leading-snug">{subtitle}</p>}
        </div>
      </div>
      <div className={locked ? 'opacity-90' : ''}>{children}</div>
    </div>
  );
}

function VIPLockedCTA({ onUpgrade, testId }) {
  return (
    <button
      onClick={onUpgrade}
      data-testid={testId}
      className="w-full flex items-center justify-between gap-2 bg-gradient-to-r from-amber-50 to-red-50 border border-amber-200/60 rounded-xl px-4 py-3 hover:shadow-sm transition-all"
    >
      <span className="flex items-center gap-2">
        <Lock size={15} strokeWidth={2.5} className="text-amber-800" />
        <span className="text-[14px] font-semibold text-zinc-900">Unlock with VIP</span>
      </span>
      <ArrowRight size={16} className="text-zinc-600" />
    </button>
  );
}

function PrimaryButton({ onClick, loading, children, disabled, testId }) {
  return (
    <button
      onClick={onClick}
      disabled={loading || disabled}
      data-testid={testId}
      className="inline-flex items-center gap-2 bg-zinc-900 text-white text-[13px] font-semibold px-4 py-2 rounded-full hover:bg-zinc-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
    >
      {loading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={13} strokeWidth={2.5} />}
      {children}
    </button>
  );
}

function SecondaryButton({ onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-zinc-700 hover:text-zinc-900 px-3 py-2 rounded-full hover:bg-black/[0.04] transition-all"
    >
      <RefreshCw size={12} strokeWidth={2.5} /> {children}
    </button>
  );
}

/* ------------------------------------------------------------------ */
/* Individual feature panels                                            */
/* ------------------------------------------------------------------ */

function ProfileReviewPanel({ locked, onUpgrade }) {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  async function run(refresh = false) {
    setLoading(true);
    try {
      const r = await api.post(`/ai-profile/review${refresh ? '?force_refresh=1' : ''}`);
      setResult(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || 'Failed');
    } finally { setLoading(false); }
  }

  if (locked) return <VIPLockedCTA onUpgrade={onUpgrade} testId="review-unlock" />;

  const focusAreas = result?.result?.focus_areas || [];

  return (
    <div className="space-y-3">
      {!result && (
        <PrimaryButton onClick={() => run(false)} loading={loading} testId="run-profile-review">
          Run AI Profile Review
        </PrimaryButton>
      )}
      {result && (
        <div className="space-y-3">
          <div className="text-[14px] text-zinc-900 font-medium leading-snug" data-testid="review-verdict">
            {result.result?.overall_verdict}
          </div>
          <div className="space-y-2">
            {focusAreas.map((f, i) => (
              <div key={i} className="border border-black/[0.08] rounded-xl p-3" data-testid={`review-focus-${i}`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-700">{f.area}</span>
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${f.priority === 'high' ? 'bg-red-100 text-red-800' : f.priority === 'medium' ? 'bg-amber-100 text-amber-800' : 'bg-zinc-100 text-zinc-700'}`}>{f.priority}</span>
                </div>
                <div className="text-[13px] text-zinc-900 font-medium">{f.issue}</div>
                <div className="text-[13px] text-zinc-700 mt-1">{f.recommendation}</div>
              </div>
            ))}
          </div>
          <SecondaryButton onClick={() => run(true)} testId="review-refresh">Re-analyze</SecondaryButton>
        </div>
      )}
    </div>
  );
}

function BioCoachPanel({ locked, onUpgrade }) {
  const [tone, setTone] = useState('authentic');
  const [analyzeResult, setAnalyzeResult] = useState(null);
  const [improveResult, setImproveResult] = useState(null);
  const [genResult, setGenResult] = useState(null);
  const [loading, setLoading] = useState('');

  async function call(kind) {
    setLoading(kind);
    try {
      let r;
      if (kind === 'analyze') r = await api.post('/ai-profile/bio/analyze', {});
      if (kind === 'improve') r = await api.post('/ai-profile/bio/improve', { tone });
      if (kind === 'generate') r = await api.post('/ai-profile/bio/generate', { tone });
      if (kind === 'analyze') setAnalyzeResult(r.data);
      if (kind === 'improve') setImproveResult(r.data);
      if (kind === 'generate') setGenResult(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || 'Failed');
    } finally { setLoading(''); }
  }

  if (locked) return <VIPLockedCTA onUpgrade={onUpgrade} testId="bio-unlock" />;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <PrimaryButton onClick={() => call('analyze')} loading={loading === 'analyze'} testId="bio-analyze-btn">
          Analyze My Bio
        </PrimaryButton>
        <PrimaryButton onClick={() => call('improve')} loading={loading === 'improve'} testId="bio-improve-btn">
          Improve My Bio
        </PrimaryButton>
        <PrimaryButton onClick={() => call('generate')} loading={loading === 'generate'} testId="bio-generate-btn">
          Write One For Me
        </PrimaryButton>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[12px] text-zinc-600 font-medium">Tone:</span>
        {['authentic', 'funny', 'confident', 'playful', 'romantic', 'direct'].map(t => (
          <button key={t} onClick={() => setTone(t)}
            data-testid={`bio-tone-${t}`}
            className={`text-[12px] px-2.5 py-1 rounded-full border transition-all ${tone === t ? 'bg-zinc-900 text-white border-zinc-900' : 'border-black/[0.1] text-zinc-700 hover:bg-black/[0.03]'}`}>
            {t}
          </button>
        ))}
      </div>
      {analyzeResult && (
        <div className="border border-black/[0.08] rounded-xl p-3" data-testid="bio-analyze-result">
          <div className="text-[13px] font-semibold text-zinc-900 mb-1">Verdict</div>
          <div className="text-[13px] text-zinc-700 mb-2">{analyzeResult.result?.verdict}</div>
          {analyzeResult.result?.opportunities?.length > 0 && (
            <>
              <div className="text-[11px] uppercase tracking-wider font-bold text-zinc-500 mt-2 mb-1">Opportunities</div>
              <ul className="list-disc pl-5 space-y-0.5 text-[13px] text-zinc-700">
                {analyzeResult.result.opportunities.map((o, i) => <li key={i}>{o}</li>)}
              </ul>
            </>
          )}
        </div>
      )}
      {(improveResult || genResult) && (
        <div className="space-y-2" data-testid="bio-variants">
          {(improveResult?.result?.variants || genResult?.result?.variants || []).map((v, i) => (
            <div key={i} className="border border-black/[0.08] rounded-xl p-3 space-y-1.5">
              <div className="text-[14px] text-zinc-900">{v.bio || v.answer}</div>
              {v.why && <div className="text-[11px] text-zinc-500">{v.why}</div>}
              <button
                data-testid={`bio-use-${i}`}
                onClick={() => toast.info("Open your Bio editor to paste this — we don't auto-apply.")}
                className="text-[12px] font-semibold text-red-600 hover:text-red-800"
              >
                Use This Bio →
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PhotoLineupPanel({ locked, onUpgrade }) {
  const [orderRes, setOrderRes] = useState(null);
  const [mixRes, setMixRes] = useState(null);
  const [loading, setLoading] = useState('');

  async function run(kind) {
    setLoading(kind);
    try {
      const r = await api.post(`/ai-profile/photos/${kind}`);
      if (kind === 'order') setOrderRes(r.data);
      else setMixRes(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || 'Failed');
    } finally { setLoading(''); }
  }

  if (locked) return <VIPLockedCTA onUpgrade={onUpgrade} testId="photo-lineup-unlock" />;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <PrimaryButton onClick={() => run('order')} loading={loading === 'order'} testId="photo-order-btn">
          Optimize Photo Order
        </PrimaryButton>
        <PrimaryButton onClick={() => run('mix')} loading={loading === 'mix'} testId="photo-mix-btn">
          Analyze Photo Mix
        </PrimaryButton>
      </div>
      {orderRes && (
        <div className="border border-black/[0.08] rounded-xl p-3" data-testid="photo-order-result">
          <div className="text-[11px] uppercase tracking-wider font-bold text-zinc-500 mb-1">Current order</div>
          <div className="text-[14px] font-mono text-zinc-800 mb-2">{(orderRes.result?.current_order || []).join(' · ')}</div>
          <div className="text-[11px] uppercase tracking-wider font-bold text-zinc-500 mb-1">AI recommended</div>
          <div className="text-[14px] font-mono text-zinc-800 mb-2">{(orderRes.result?.recommended_order || []).join(' · ')}</div>
          <div className="text-[13px] text-zinc-700 mt-2">
            <strong>Photo #{orderRes.result?.lead_photo_index}</strong> — {orderRes.result?.reason}
          </div>
          <button
            onClick={() => toast.info("Open your Photo editor to reorder — we don't auto-apply.")}
            data-testid="apply-recommended-order"
            className="mt-3 inline-flex items-center gap-1.5 text-[12px] font-semibold text-red-600 hover:text-red-800"
          >
            Apply Recommended Order <ArrowRight size={12} />
          </button>
        </div>
      )}
      {mixRes && (
        <div className="border border-black/[0.08] rounded-xl p-3" data-testid="photo-mix-result">
          <div className="text-[13px] font-medium text-zinc-900 mb-1">{mixRes.result?.verdict}</div>
          {(mixRes.result?.issues || []).length > 0 && (
            <ul className="list-disc pl-5 text-[13px] text-zinc-700 space-y-0.5 mt-2">
              {mixRes.result.issues.map((s, i) => <li key={i}>{s}</li>)}
            </ul>
          )}
          {(mixRes.result?.recommendations || []).length > 0 && (
            <>
              <div className="text-[11px] uppercase tracking-wider font-bold text-zinc-500 mt-3 mb-1">Suggestions</div>
              <ul className="list-disc pl-5 text-[13px] text-zinc-700 space-y-0.5">
                {mixRes.result.recommendations.map((s, i) => <li key={i}>{s}</li>)}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function OptimizerPanel({ locked, onUpgrade }) {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState({});

  async function run() {
    setLoading(true);
    try {
      const r = await api.post('/ai-profile/optimizer');
      setPlan(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || 'Failed');
    } finally { setLoading(false); }
  }

  if (locked) return <VIPLockedCTA onUpgrade={onUpgrade} testId="optimizer-unlock" />;

  const changes = plan?.result?.proposed_changes || [];
  const selectedCount = Object.values(selected).filter(Boolean).length;

  return (
    <div className="space-y-3">
      {!plan && (
        <PrimaryButton onClick={run} loading={loading} testId="run-optimizer">
          Optimize My Profile
        </PrimaryButton>
      )}
      {plan && (
        <>
          {plan.result?.summary && (
            <div className="text-[13px] text-zinc-700 italic">{plan.result.summary}</div>
          )}
          <div className="space-y-2">
            {changes.map((c) => (
              <label key={c.id} className="flex items-start gap-3 border border-black/[0.08] rounded-xl p-3 cursor-pointer hover:bg-black/[0.02]" data-testid={`optim-change-${c.id}`}>
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={!!selected[c.id]}
                  onChange={(e) => setSelected({ ...selected, [c.id]: e.target.checked })}
                  data-testid={`optim-check-${c.id}`}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-700">{c.type?.replace(/_/g, ' ')}</span>
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${c.impact === 'high' ? 'bg-red-100 text-red-800' : c.impact === 'medium' ? 'bg-amber-100 text-amber-800' : 'bg-zinc-100 text-zinc-700'}`}>{c.impact}</span>
                  </div>
                  <div className="text-[13px] text-zinc-900">{c.why}</div>
                  {c.proposed && (
                    <div className="text-[12px] text-zinc-600 mt-1 line-clamp-2">→ {typeof c.proposed === 'string' ? c.proposed : JSON.stringify(c.proposed)}</div>
                  )}
                </div>
              </label>
            ))}
          </div>
          <button
            disabled={selectedCount === 0}
            onClick={() => toast.info(`Selected ${selectedCount} change(s). Open the relevant editor to apply — nothing auto-changes on your profile.`)}
            data-testid="apply-selected-changes"
            className="inline-flex items-center gap-2 bg-red-600 text-white text-[13px] font-semibold px-4 py-2 rounded-full hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
          >
            <CheckCircle2 size={14} strokeWidth={2.5} /> Apply Selected Changes ({selectedCount})
          </button>
        </>
      )}
    </div>
  );
}

function EnhanceGeneratePanel({ locked, onUpgrade }) {
  if (locked) return <VIPLockedCTA onUpgrade={onUpgrade} testId="photo-genenh-unlock" />;
  return (
    <div className="space-y-2">
      <p className="text-[13px] text-zinc-700 leading-relaxed">
        Photo Enhancement and AI Dating Photos are available from the Photo Editor.
        Open any photo to enhance it, or tap <strong>AI Photos</strong> in the photo grid
        to generate new dating-profile photos from your existing references.
      </p>
      <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
        AI Photo Enhancement is a QUALITY tool — not a beauty filter. AI-generated photos
        must be explicitly approved before they go public and are always labelled as AI-generated.
      </p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Main page                                                            */
/* ------------------------------------------------------------------ */
export default function AIProfileCoach() {
  const navigate = useNavigate();
  const [access, setAccess] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/ai-profile/access')
       .then(r => setAccess(r.data))
       .catch(() => setAccess({ has_access: false, current_tier: 'free' }))
       .finally(() => setLoading(false));
  }, []);

  const locked = !access?.has_access;
  const onUpgrade = () => navigate('/subscription?feature=ai_profile_optim');

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: APP_BG }}>
        <Loader2 className="animate-spin text-zinc-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-16" style={{ background: APP_BG }}>
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white/95 backdrop-blur border-b border-black/[0.06]">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate('/profile')} data-testid="ai-coach-back" className="text-zinc-600 hover:text-zinc-900">
            <ArrowLeft size={20} />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-[17px] font-bold text-zinc-900 flex items-center gap-2">
              <Sparkles size={16} style={{ color: RED }} strokeWidth={2.5} />
              AI Profile Coach
            </h1>
            <p className="text-[11px] text-zinc-500">
              {locked
                ? 'Unlock personalized AI recommendations with VIP.'
                : `${access?.limits?.text_features?.cap - access?.limits?.text_features?.used} text · ${access?.limits?.photo_enhancements?.cap - access?.limits?.photo_enhancements?.used} enhance · ${access?.limits?.photo_generations?.cap - access?.limits?.photo_generations?.used} photos remaining`}
            </p>
          </div>
          {access?.current_tier === 'vip' && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-900">
              <Crown size={10} strokeWidth={2.5} /> VIP
            </span>
          )}
        </div>
      </div>

      {/* Sections */}
      <div className="max-w-2xl mx-auto px-4 py-4 space-y-4">
        {locked && (
          <div className="bg-gradient-to-br from-amber-50 via-red-50 to-white rounded-2xl border border-amber-200/60 p-5" data-testid="ai-coach-vip-teaser">
            <div className="flex items-center gap-2 mb-2">
              <Crown size={18} className="text-amber-800" />
              <h2 className="text-[16px] font-bold text-zinc-900">Unlock AI Profile Coach</h2>
            </div>
            <p className="text-[13px] text-zinc-700 leading-relaxed mb-3">
              Get AI recommendations for your photos, bio, prompts, and overall profile —
              plus AI Photo Enhancement and AI Dating Photos. All VIP-only.
            </p>
            <button onClick={onUpgrade} data-testid="ai-coach-upgrade-cta" className="bg-zinc-900 text-white text-[13px] font-semibold px-4 py-2 rounded-full hover:bg-zinc-800 transition-all">
              Upgrade to VIP →
            </button>
          </div>
        )}

        <Section icon={Sparkles} title="AI Profile Review"
          subtitle="High-impact focus areas across your photos, bio, prompts, and completeness."
          locked={locked} testId="section-review">
          <ProfileReviewPanel locked={locked} onUpgrade={onUpgrade} />
        </Section>

        <Section icon={Camera} title="Photo Lineup — Order + Mix"
          subtitle="Best-order recommendation + variety check for your photo lineup."
          locked={locked} testId="section-photo-lineup">
          <PhotoLineupPanel locked={locked} onUpgrade={onUpgrade} />
        </Section>

        <Section icon={FileText} title="AI Bio Coach"
          subtitle="Analyze, rewrite, or draft a bio — 3 variants each time, tone-controlled."
          locked={locked} testId="section-bio">
          <BioCoachPanel locked={locked} onUpgrade={onUpgrade} />
        </Section>

        <Section icon={MessageSquare} title="AI Prompt Coach"
          subtitle="Open any prompt from your Profile editor to analyze, improve, or draft an answer."
          locked={locked} testId="section-prompts">
          {locked
            ? <VIPLockedCTA onUpgrade={onUpgrade} testId="prompt-unlock" />
            : <p className="text-[13px] text-zinc-700">Open a prompt in your <button onClick={() => navigate('/profile#prompts')} className="text-red-600 hover:underline font-semibold">Profile editor</button> to see AI options for each.</p>
          }
        </Section>

        <Section icon={Wand2} title="AI Profile Optimizer"
          subtitle="A concrete change list — approve each one individually before it hits your profile."
          locked={locked} testId="section-optimizer">
          <OptimizerPanel locked={locked} onUpgrade={onUpgrade} />
        </Section>

        <Section icon={ImagePlus} title="AI Photos — Enhance & Generate"
          subtitle="Conservative photo quality fixes + AI-generated dating photos from your reference shots."
          locked={locked} testId="section-photo-gen">
          <EnhanceGeneratePanel locked={locked} onUpgrade={onUpgrade} />
        </Section>
      </div>
    </div>
  );
}
